import React, { useCallback, useContext, useMemo, useState } from 'react';
import InputField from 'components/InputField';
import useCallbackRef from 'hooks/useCallbackRef';
import { t } from 'i18next';
import { Modal, Button, Loader } from 'ncoded-component-library';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { Form, Field } from 'react-final-form';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import validations from 'validations';

type AddFolderProps = {
  id: string;
  currentlySelectedFolder?: string;
  onAddFolderSubmit?: (node: DataRoomNode) => void;
};

const useAddFolder = ({
  id,
  currentlySelectedFolder,
  onAddFolderSubmit,
}: AddFolderProps) => {
  const { popServerError } = useContext(PopNotificationsContext);

  const [isModalLoading, setIsModalLoading] = useState(false);
  const [addAFolderModal, addAFolderModalRef] = useCallbackRef<OverlayRef>();

  const onFormSubmit = useCallback(
    async (values) => {
      setIsModalLoading(true);
      try {
        const { data } = await dataRoomApi.nodes.createNode(
          id,
          values.name,
          currentlySelectedFolder,
        );

        onAddFolderSubmit?.(data);
        addAFolderModal.close();
      } catch (e) {
        popServerError(e);
      } finally {
        setIsModalLoading(false);
      }
    },
    [
      addAFolderModal,
      currentlySelectedFolder,
      id,
      onAddFolderSubmit,
      popServerError,
    ],
  );

  const AddFolderModal = useMemo(
    () => (
      <Modal
        ref={addAFolderModalRef}
        className="data-room-add-folder-modal"
        title={t('createAFolder')}
      >
        <Form
          onSubmit={onFormSubmit}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <Field
                name="name"
                label={t('folderName')}
                placeholder={t('name')}
                component={InputField}
                required
                validate={validations.required()}
              />

              <div className="actions">
                <Button variant="link" onClick={addAFolderModal?.close}>
                  {t('cancel')}
                </Button>
                <Button type="submit">{t('confirm')}</Button>
              </div>
            </form>
          )}
        />
        {isModalLoading && <Loader />}
      </Modal>
    ),
    [addAFolderModal?.close, addAFolderModalRef, isModalLoading, onFormSubmit],
  );

  return { addAFolderModal, AddFolderModal };
};

export default useAddFolder;
